import { GameState } from '../game/GameState';
import type { MissionData, MissionObjectiveData } from '../types/data';
import type { MissionState } from '../types/game';
import { DataRegistry } from './DataRegistry';
import { ModifierManager } from './ModifierManager';
import { NavalManager } from './NavalManager';
import { TradeManager } from './TradeManager';

export class MissionManager {
  static readonly instance = new MissionManager();

  ensureCampaignInitialized() {
    const campaign = GameState.instance.requireCampaign();
    campaign.missionStates ??= {};
    campaign.missionHistory ??= [];
    for (const faction of Object.values(campaign.factions)) {
      for (const mission of this.applicableMissions(faction.id)) {
        const key = this.stateKey(faction.id, mission.id);
        campaign.missionStates[key] ??= {
          id: mission.id,
          factionId: faction.id,
          status: this.prerequisitesMet(faction.id, mission) ? 'available' : 'locked',
          progress: mission.objectives.map(() => 0),
        };
      }
    }
  }

  processMonth() {
    this.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();
    for (const faction of Object.values(campaign.factions)) {
      for (const mission of this.applicableMissions(faction.id)) {
        const state = this.state(faction.id, mission.id);
        if (!state || state.status === 'claimed' || state.status === 'failed') continue;
        if (state.status === 'locked' && this.prerequisitesMet(faction.id, mission)) state.status = 'available';
        if (faction.id !== campaign.playerFactionId && state.status === 'available') this.startMission(faction.id, mission.id, false);
        if (state.status !== 'active') continue;
        state.progress = mission.objectives.map((objective) => this.objectiveProgress(faction.id, objective));
        if (mission.objectives.every((objective, index) => (state.progress[index] ?? 0) >= objective.value)) {
          state.status = 'completed';
          state.completedTurn = campaign.turn;
          GameState.instance.log(`${DataRegistry.instance.factions[faction.id]?.name ?? faction.id} completed mission: ${mission.title}.`, faction.id === campaign.playerFactionId ? 'success' : 'info');
          if (faction.id !== campaign.playerFactionId) this.claimReward(faction.id, mission.id, false);
        }
      }
    }
    this.updateMissionVictory();
  }

  applicableMissions(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    return DataRegistry.instance.missionList().filter((mission) => {
      if (mission.factionIds?.length && !mission.factionIds.includes(factionId)) return false;
      if (mission.scenarioIds?.length && !mission.scenarioIds.includes(campaign.scenarioId)) return false;
      return true;
    });
  }

  startMission(factionId: string, missionId: string, log = true) {
    this.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();
    const mission = DataRegistry.instance.missions[missionId];
    const state = this.state(factionId, missionId);
    if (!mission || !state || !['available', 'active'].includes(state.status)) return false;
    state.status = 'active';
    state.startedTurn ??= campaign.turn;
    state.progress = mission.objectives.map((objective) => this.objectiveProgress(factionId, objective));
    if (log) GameState.instance.log(`Mission begun: ${mission.title}.`, 'success');
    return true;
  }

  claimReward(factionId: string, missionId: string, log = true) {
    this.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();
    const mission = DataRegistry.instance.missions[missionId];
    const state = this.state(factionId, missionId);
    const faction = campaign.factions[factionId];
    if (!mission || !state || !faction || state.status !== 'completed') return false;
    const multiplier = Math.max(1, ModifierManager.instance.forFaction(factionId).missionRewardMultiplier);
    const rounded = (value: number) => Math.round(value * multiplier);
    const rewards = mission.rewards;
    faction.treasury += rounded(Number(rewards.gold ?? 0));
    faction.prestige = Math.min(100, faction.prestige + rounded(Number(rewards.prestige ?? 0)));
    faction.legitimacy = Math.min(100, faction.legitimacy + rounded(Number(rewards.legitimacy ?? 0)));
    faction.manpower = Math.min(faction.maxManpower, faction.manpower + rounded(Number(rewards.manpower ?? 0)));
    faction.militaryTradition = Math.min(100, faction.militaryTradition + rounded(Number(rewards.militaryTradition ?? 0)));
    faction.navalTradition = Math.min(100, faction.navalTradition + rounded(Number(rewards.navalTradition ?? 0)));
    faction.counterIntelligence = Math.min(100, faction.counterIntelligence + rounded(Number(rewards.counterIntelligence ?? 0)));
    faction.religiousAuthority = Math.min(100, faction.religiousAuthority + rounded(Number(rewards.religiousAuthority ?? 0)));
    const research = rounded(Number(rewards.research ?? 0));
    faction.research.knowledge += research;
    faction.research.activeProgress += research;
    state.status = 'claimed';
    state.claimedTurn = campaign.turn;
    const rewardSummary = this.rewardSummary(mission);
    campaign.missionHistory.unshift({ id: `mission_${factionId}_${missionId}_${campaign.turn}`, missionId, factionId, turn: campaign.turn, title: mission.title, rewardSummary });
    campaign.missionHistory = campaign.missionHistory.slice(0, 80);
    if (log) GameState.instance.log(`Mission reward claimed: ${mission.title} — ${rewardSummary}.`, 'success');
    for (const next of this.applicableMissions(factionId)) {
      const nextState = this.state(factionId, next.id);
      if (nextState?.status === 'locked' && this.prerequisitesMet(factionId, next)) nextState.status = 'available';
    }
    this.updateMissionVictory();
    return true;
  }

  state(factionId: string, missionId: string) {
    return GameState.instance.requireCampaign().missionStates[this.stateKey(factionId, missionId)];
  }

  objectiveProgress(factionId: string, objective: MissionObjectiveData) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    if (!faction) return 0;
    const provinces = Object.values(campaign.provinces).filter((province) => province.owner === factionId);
    switch (objective.type) {
      case 'own_provinces': return provinces.length;
      case 'own_ports': return provinces.filter((province) => DataRegistry.instance.provinces[province.id]?.port).length;
      case 'treasury': return faction.treasury;
      case 'prestige': return faction.prestige;
      case 'legitimacy': return faction.legitimacy;
      case 'religious_authority': return faction.religiousAuthority;
      case 'researched_techs': return faction.researchedTechs.length;
      case 'researched_tech': return objective.target && faction.researchedTechs.includes(objective.target) ? 1 : 0;
      case 'adopted_institutions': return faction.research.adoptedInstitutions.length;
      case 'scholars': return faction.research.scholars;
      case 'accepted_cultures': return faction.acceptedCultures.length;
      case 'naval_doctrine': return faction.research.navalDoctrineId ? 1 : 0;
      case 'land_doctrine': return faction.research.landDoctrineId ? 1 : 0;
      case 'state_religion_provinces': return provinces.filter((province) => province.religion === faction.stateReligion).length;
      case 'hold_province': return objective.target && campaign.provinces[objective.target]?.owner === factionId ? 1 : 0;
      case 'fort_levels': return provinces.reduce((sum, province) => sum + (province.fortLevel ?? 0), 0);
      case 'average_prosperity': return provinces.length ? provinces.reduce((sum, province) => sum + province.prosperity, 0) / provinces.length : 0;
      case 'army_strength': return Object.values(campaign.armies).filter((army) => army.factionId === factionId).reduce((sum, army) => sum + army.units.reduce((unitSum, unit) => unitSum + unit.strength, 0), 0);
      case 'fleet_strength': return Object.values(campaign.fleets).filter((fleet) => fleet.factionId === factionId).reduce((sum, fleet) => sum + NavalManager.instance.fleetStrength(fleet.id), 0);
      case 'active_treaties': return new Set(Object.values(faction.treaties).flat()).size;
      case 'subjects': return Object.values(campaign.subjectContracts).filter((contract) => contract.overlordFactionId === factionId).length;
      case 'trade_value': return TradeManager.instance.routeSummaries().filter((route) => route.owner === factionId).reduce((sum, route) => sum + route.value, 0);
      case 'spy_network_strength': return Math.max(0, ...Object.values(campaign.spyNetworks).filter((network) => network.sourceFactionId === factionId).map((network) => network.strength));
      case 'completed_operations': return campaign.espionageHistory.filter((entry) => entry.sourceFactionId === factionId && entry.outcome === 'success').length;
      case 'survive_turns': return campaign.turn;
      default: return 0;
    }
  }

  rewardSummary(mission: MissionData) {
    const labels: Record<string, string> = { gold: 'gold', prestige: 'prestige', legitimacy: 'legitimacy', manpower: 'manpower', research: 'research', navalTradition: 'naval tradition', militaryTradition: 'military tradition', counterIntelligence: 'counter-intelligence', religiousAuthority: 'religious authority' };
    return Object.entries(mission.rewards).filter(([, value]) => Number(value) !== 0).map(([key, value]) => `${value} ${labels[key] ?? key}`).join(', ') || 'strategic recognition';
  }

  private prerequisitesMet(factionId: string, mission: MissionData) {
    return (mission.prerequisites ?? []).every((missionId) => this.state(factionId, missionId)?.status === 'claimed');
  }

  private updateMissionVictory() {
    const campaign = GameState.instance.requireCampaign();
    const factionId = campaign.playerFactionId;
    const missions = this.applicableMissions(factionId);
    const claimed = missions.filter((mission) => this.state(factionId, mission.id)?.status === 'claimed').length;
    campaign.victory.mission = Math.min(100, Math.round(claimed / Math.max(1, Math.min(8, missions.length)) * 100));
  }

  private stateKey(factionId: string, missionId: string) { return `${factionId}:${missionId}`; }
}